import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw } from 'lucide-react';
import API from '../../api/axiosInstance';
import { utcToLADateKey } from '../../utils/laDateUtils';
import { assignGroupsToPeriods } from '../../utils/bookingCoverage';
import RoomSegmentCard from './RoomSegmentCard';
import StayTimelineRow from './StayTimelineRow';
import BookRoomModal from './BookRoomModal';
import '../css/ParticipantBookingTab.css';
import { GoldLoader } from '../shared/Loaders';

/** Room-count based, matching the design handoff exactly: "Not booked" or "N room(s) booked". */
export const coverageBadge = (roomCount) => {
  if (roomCount === 0) {
    return { label: 'Not booked', className: 'pbt-badge-pill pbt-badge-pill--empty', dotClassName: 'pbt-status-dot--empty' };
  }
  return {
    label: `${roomCount} room${roomCount !== 1 ? 's' : ''} booked`,
    className: 'pbt-badge-pill pbt-badge-pill--full',
    dotClassName: 'pbt-status-dot--full',
  };
};

const ParticipantBookingTab = ({ participant, onNavigateToTab }) => {
  const stayPeriods = useMemo(() => participant?.stay_periods || [], [participant?.stay_periods]);
  const competitions = participant?.competition_id || [];
  const personId = participant?.person_id?._id;
  const participantName = `${participant?.person_id?.name || ''} ${participant?.person_id?.lname || ''}`.trim();

  const [selectedCompetitionId, setSelectedCompetitionId] = useState('');
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState('');
  const [guests, setGuests] = useState([]);
  // First stay open by default; each toggles independently (not an accordion).
  const [openStayIndices, setOpenStayIndices] = useState(() => new Set([0]));
  const [panelTarget, setPanelTarget] = useState(null); // index of stay being booked, or null

  useEffect(() => {
    const first = competitions[0];
    setSelectedCompetitionId(first?._id || first || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?._id]);

  const fetchGroups = useCallback(async () => {
    if (!personId) return;
    setLoadingGroups(true);
    setGroupsError('');
    try {
      const res = await API.get('/ems/room-bookings/grouped', { params: { participant_id: personId } });
      setGroups(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setGroupsError(err.response?.data?.message || 'Failed to load bookings');
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  }, [personId]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const fetchGuests = useCallback(async () => {
    if (!participant?._id) return;
    try {
      const res = await API.get(`/ems/participants/${participant._id}/guests`);
      setGuests(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setGuests([]);
    }
  }, [participant?._id]);

  useEffect(() => {
    fetchGuests();
  }, [fetchGuests]);

  const { periodGroups, orphanGroups } = useMemo(
    () => assignGroupsToPeriods(groups, stayPeriods),
    [groups, stayPeriods]
  );

  return (
    <div className="pbt-root">
      {competitions.length > 1 && (
        <div className="pbt-competition-select">
          <label className="pbt-label" htmlFor="pbt-competition">Booking for</label>
          <select
            id="pbt-competition"
            className="form-select form-select-sm pbt-select"
            value={selectedCompetitionId}
            onChange={(e) => setSelectedCompetitionId(e.target.value)}
          >
            {competitions.map((c) => {
              const id = c?._id || c;
              const name = c?.name || 'Competition';
              return <option key={id} value={id}>{name}</option>;
            })}
          </select>
        </div>
      )}

      <section className="pbt-section pbt-section--accommodation">
        <div className="pbt-section__header pbt-section__header--plain">
          <h2 className="pbt-section__title pbt-section__title--room-bookings">Room Bookings</h2>
        </div>

        {groupsError && (
          <div className="pbt-panel__error-banner">
            <span>{groupsError}</span>
            <button type="button" className="btn btn-sm pbt-btn-retry" onClick={fetchGroups}>
              <RefreshCw size={12} /> Retry
            </button>
          </div>
        )}

        {loadingGroups && !groupsError && (
          <GoldLoader label="Loading..." variant="section" />
        )}

        {stayPeriods.length === 0 ? (
          <div className="pbt-no-stays">
            <p className="mb-1 fw-medium">No stay periods defined for this participant.</p>
            <p className="text-muted mb-2" style={{ fontSize: '0.85rem' }}>
              Add check-in / check-out windows via the Registration tab before booking a room.
            </p>
            {onNavigateToTab && (
              <button type="button" className="btn btn-link p-0 pbt-empty-cta" onClick={() => onNavigateToTab('REGISTRATION')}>
                Go to Registration tab →
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="pbt-section__subheader">
              <p className="pbt-section__description">
                These are the accommodation dates requested by the participant. Select one to view and book rooms in these.
              </p>
            </div>
            <div className="pbt-timeline-list">
              {stayPeriods.map((p, i) => (
                <StayTimelineRow
                  key={i}
                  period={p}
                  index={i}
                  assignedGroups={periodGroups.get(i) || []}
                  isOpen={openStayIndices.has(i)}
                  onToggle={() =>
                    setOpenStayIndices((cur) => {
                      const next = new Set(cur);
                      if (next.has(i)) next.delete(i);
                      else next.add(i);
                      return next;
                    })
                  }
                  onBookRoom={() => setPanelTarget(i)}
                  competitionId={selectedCompetitionId}
                  participantId={personId}
                  participantName={participantName}
                  guests={guests}
                  onChanged={fetchGroups}
                />
              ))}
            </div>
          </>
        )}

        {panelTarget !== null && stayPeriods[panelTarget] && (
          <BookRoomModal
            show
            stayIndex={panelTarget}
            totalStays={stayPeriods.length}
            stayInKey={utcToLADateKey(stayPeriods[panelTarget].check_in_date)}
            stayOutKey={utcToLADateKey(stayPeriods[panelTarget].check_out_date)}
            competitionId={selectedCompetitionId}
            participantId={personId}
            participantName={participantName}
            existingSegments={periodGroups.get(panelTarget) || []}
            guests={guests}
            onDone={() => { setPanelTarget(null); fetchGroups(); }}
            onHide={() => setPanelTarget(null)}
          />
        )}

        {orphanGroups.length > 0 && (
          <div className="pbt-orphan-section">
            <p className="pbt-orphan-section__label">
              Other bookings — not matching a defined stay period
            </p>
            {orphanGroups.map((group) => (
              <RoomSegmentCard
                key={group.booking_ids[0]}
                group={group}
                competitionId={selectedCompetitionId}
                participantId={personId}
                participantName={participantName}
                guests={guests}
                onChanged={fetchGroups}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default ParticipantBookingTab;
